import AutoCompleteChip from '@components/standardAutoCompleteSearch/AutoCompleteChip'
import StandardAutoCompleteSearch, {
  AutoCompleteOption,
  AutoCompleteRenderChipProps,
  AutoCompleteResultsZIndexPlacement,
} from '@components/standardAutoCompleteSearch/StandardAutoCompleteSearch'

import { colors } from '@globals/themes/defaultTheme'
import {
  FederatedSearchRecipientsParams,
  FederatedSearchRecipientsResults,
  TeamDisplayData,
  TenantMemberDisplayData,
  TenantMemberOrTeamDisplayData,
} from '@kudos/http-client'
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import UserNameDisplay from '@shared/userDisplay/UserNameDisplay'
import { t } from '@transifex/native'
import { T } from '@transifex/react'
import { ReactNode, useMemo, useState } from 'react'
import {
  Control,
  Controller,
  FieldName,
  FieldPathValue,
  FieldValues,
  Path,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import { fullNameFromParts } from '@shared/utils/nameFormatting'
import ErrorText from '../ErrorText'
import { generateTeamName, getTeamIcon } from '../teamsAutoComplete/utils'
import { getRecipientAvatar, getRecipientChipLabel } from './recipientDisplayUtils'
import useSearchTenantMembersAndTeams from './useSearchTenantMembersAndTeams'

const isTeamValue = (value: TenantMemberOrTeamDisplayData): value is TeamDisplayData =>
  typeof value === 'object' && value !== null && 'teamType' in value

const renderChip = (
  option: AutoCompleteOption<TenantMemberOrTeamDisplayData>,
  props: AutoCompleteRenderChipProps,
  showAvatars?: boolean,
) => {
  const { key, ...otherProps } = props
  const isTeam = isTeamValue(option.value)
  if (isTeam) {
    const team = option.value as TeamDisplayData
    return (
      <AutoCompleteChip
        key={key}
        {...otherProps}
        label={getRecipientChipLabel(option.value)}
        icon={!showAvatars ? getTeamIcon(team.teamType) : undefined}
        avatar={showAvatars ? getRecipientAvatar(option.value) : undefined}
      />
    )
  } else {
    return (
      <AutoCompleteChip
        key={key}
        {...otherProps}
        translate="no"
        label={getRecipientChipLabel(option.value)}
        avatar={showAvatars ? getRecipientAvatar(option.value) : undefined}
      />
    )
  }
}

export const renderOption = (
  props: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
  option: AutoCompleteOption<TenantMemberOrTeamDisplayData>,
) => {
  const isTeam = isTeamValue(option.value)
  const { key, ...listItemProps } = props

  return (
    <Stack
      {...listItemProps}
      key={key}
      component="li"
      direction="row"
      useFlexGap
      sx={{
        gap: 1,
        '& svg': {
          color: colors.grayDark,
        },
      }}>
      {isTeam ? getTeamIcon((option.value as TeamDisplayData).teamType) : <PersonOutlineIcon />}
      {isTeam ? (
        <Typography>
          {generateTeamName(
            (option.value as TeamDisplayData).name,
            (option.value as TeamDisplayData).segmentType,
          )}
        </Typography>
      ) : (
        <Typography translate="no">
          <UserNameDisplay user={option.value as TenantMemberDisplayData} />
        </Typography>
      )}
      {isTeam && (option.value as TeamDisplayData).activeMemberCount && (
        <Typography variant="caption">
          <T
            _str="{activeMemberCountNum, plural, one {{activeMemberCount} user} other {{activeMemberCount} users}}"
            activeMemberCountNum={(option.value as TeamDisplayData).activeMemberCount}
            activeMemberCount={(
              (option.value as TeamDisplayData).activeMemberCount ?? 0
            ).toLocaleString()}
          />
        </Typography>
      )}
    </Stack>
  )
}

export type TenantMembersAndTeamsSearchFieldProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  label?: ReactNode
  /** Federated team + member search; results omit already-selected recipient IDs. Unused when `paginated`. */
  searchFn: (params: FederatedSearchRecipientsParams) => Promise<FederatedSearchRecipientsResults>
  /** Optional delegator for recognition flows that search on someone else's behalf. */
  delegatorId?: string
  showAvatars?: boolean
  disabled?: boolean
  zIndexPlacement?: AutoCompleteResultsZIndexPlacement
  /** Open with a first page of 50 and lazy-load via identity cursor search. */
  paginated?: boolean
}

const appliedFilterLabel = (option: AutoCompleteOption<TenantMemberOrTeamDisplayData>) => {
  if (isTeamValue(option.value)) {
    return generateTeamName(option.value.name, option.value.segmentType)
  }
  const member = option.value as TenantMemberDisplayData
  return fullNameFromParts(member.firstName, member.lastName)
}

/**
 * Multi-select autocomplete for teams and tenant members, wired to react-hook-form.
 *
 * Form value is `TenantMemberOrTeamDisplayData[]`. Used for recognition recipients
 * and similar flows. {@link useSearchTenantMembersAndTeams} keeps selected IDs out
 * of the dropdown unless the field is paging; selection writes directly from
 * autocomplete option values.
 */
const TenantMembersAndTeamsSearchField = <T extends FieldValues>({
  name,
  control,
  label,
  searchFn,
  delegatorId,
  showAvatars,
  disabled,
  zIndexPlacement,
  paginated = false,
}: TenantMembersAndTeamsSearchFieldProps<T>) => {
  const [searchText, setSearchText] = useState<string>('')
  const [open, setOpen] = useState(false)
  const { setValue } = useFormContext()

  const selectedMembers = useWatch({ control, name })
  const selectedMembersIds = useMemo(
    () => selectedMembers?.map(({ id }: { id: string }) => id),
    [selectedMembers],
  )

  const {
    searchedTenantMembersAndTeams,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useSearchTenantMembersAndTeams(
    searchText,
    selectedMembersIds,
    searchFn,
    delegatorId ?? undefined,
    paginated,
    open,
  )

  const onSelectedOptionsChanged = (
    options: AutoCompleteOption<TenantMemberOrTeamDisplayData>[],
  ) => {
    const selectedOptions: FieldPathValue<FieldValues, FieldName<FieldValues>> = options.map(
      option => option.value,
    )
    setValue(name, selectedOptions, { shouldValidate: true, shouldDirty: true })
  }

  const options = useMemo(
    () =>
      searchedTenantMembersAndTeams?.map(
        (member: TenantMemberOrTeamDisplayData) =>
          ({
            avatar: showAvatars ? getRecipientAvatar(member) : undefined,
            icon: isTeamValue(member) && !showAvatars ? getTeamIcon(member.teamType) : undefined,
            value: member,
            id: member.id,
          }) as AutoCompleteOption<TenantMemberOrTeamDisplayData>,
      ) || [],
    [searchedTenantMembersAndTeams, showAvatars],
  )

  return (
    <Stack>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => (
          <>
            <StandardAutoCompleteSearch<TenantMemberOrTeamDisplayData>
              searchText={searchText}
              value={
                selectedMembers?.map((member: TenantMemberOrTeamDisplayData) => ({
                  value: member,
                  id: member.id,
                })) ?? []
              }
              options={options}
              onSearchTextChanged={setSearchText}
              onSelectedOptionsChanged={onSelectedOptionsChanged}
              placeholder={t('Search users or teams...')}
              renderChip={(option, props) => renderChip(option, props, showAvatars)}
              label={label}
              renderOption={renderOption}
              groupBy={option => (isTeamValue(option.value) ? t('Teams') : t('Users'))}
              disabled={disabled}
              zIndexPlacement={zIndexPlacement}
              loading={isFetching}
              hideDropdown={true}
              paginated={paginated}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onFetchNextPage={fetchNextPage}
              onOpenChange={setOpen}
              getAppliedFilterLabel={appliedFilterLabel}
            />
            {!!error && <ErrorText>{error.message}</ErrorText>}
          </>
        )}
      />
    </Stack>
  )
}

export default TenantMembersAndTeamsSearchField
